import { Children, useEffect, useRef, useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CardCarousel = ({ children, gap = 16, itemStyle = {} }) => {
  const items = Children.toArray(children);
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const updateActiveIndex = () => {
    const track = trackRef.current;
    if (!track || items.length === 0) return;

    const trackCenter = track.scrollLeft + (track.clientWidth / 2);
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    Array.from(track.children).forEach((child, index) => {
      const childCenter = child.offsetLeft + (child.offsetWidth / 2);
      const distance = Math.abs(trackCenter - childCenter);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    setActiveIndex(nearestIndex);
  };

  useEffect(() => {
    setActiveIndex(current => Math.min(current, Math.max(items.length - 1, 0)));
  }, [items.length]);

  const goTo = (index) => {
    const track = trackRef.current;
    const target = track?.children[index];
    if (!track || !target) return;

    track.scrollTo({
      left: target.offsetLeft,
      behavior: 'smooth',
    });
    setActiveIndex(index);
  };

  // Sliding window pagination dots (maksimal 5 titik jika item banyak)
  const visibleDotIndices = useMemo(() => {
    const total = items.length;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);

    const maxDots = 5;
    let start = activeIndex - Math.floor(maxDots / 2);
    if (start < 0) start = 0;
    if (start + maxDots > total) start = total - maxDots;

    return Array.from({ length: maxDots }, (_, i) => start + i);
  }, [items.length, activeIndex]);

  return (
    <div>
      <div
        ref={trackRef}
        onScroll={updateActiveIndex}
        style={{
          display: 'flex',
          gap,
          overflowX: 'auto',
          padding: '4px 2px 12px',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {items.map((child, index) => (
          <div
            key={child.key || index}
            style={{
              flex: '0 0 auto',
              scrollSnapAlign: 'start',
              display: 'flex',
              ...itemStyle,
            }}
          >
            {child}
          </div>
        ))}
      </div>

      {items.length > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            marginTop: 8,
            padding: '6px 0',
          }}
        >
          <button
            type="button"
            aria-label="Pengajuan sebelumnya"
            onClick={() => goTo(activeIndex - 1)}
            disabled={activeIndex === 0}
            style={navigationButtonStyle(activeIndex > 0)}
          >
            <ChevronLeft size={16} />
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              minWidth: 70,
            }}
          >
            {visibleDotIndices.map((index) => {
              const isActive = index === activeIndex;
              const isEdge =
                (index === visibleDotIndices[0] && index > 0) ||
                (index === visibleDotIndices[visibleDotIndices.length - 1] && index < items.length - 1);

              return (
                <button
                  key={index}
                  type="button"
                  aria-label={`Buka pengajuan ${index + 1}`}
                  onClick={() => goTo(index)}
                  style={{
                    width: isActive ? 22 : (isEdge ? 5 : 7),
                    height: isEdge ? 5 : 7,
                    padding: 0,
                    border: 0,
                    borderRadius: 999,
                    backgroundColor: isActive ? '#2563eb' : (isEdge ? '#cbd5e1' : '#94a3b8'),
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    flexShrink: 0,
                  }}
                />
              );
            })}
          </div>

          <button
            type="button"
            aria-label="Pengajuan berikutnya"
            onClick={() => goTo(activeIndex + 1)}
            disabled={activeIndex === items.length - 1}
            style={navigationButtonStyle(activeIndex < items.length - 1)}
          >
            <ChevronRight size={16} />
          </button>

          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#475569',
              backgroundColor: '#f1f5f9',
              padding: '3px 10px',
              borderRadius: 20,
              minWidth: 46,
              textAlign: 'center',
              userSelect: 'none',
            }}
          >
            {activeIndex + 1} / {items.length}
          </span>
        </div>
      )}
    </div>
  );
};

const navigationButtonStyle = (enabled) => ({
  width: 32,
  height: 32,
  padding: 0,
  border: `1px solid ${enabled ? '#bfdbfe' : '#f1f5f9'}`,
  borderRadius: '50%',
  backgroundColor: enabled ? '#eff6ff' : '#f8fafc',
  color: enabled ? '#2563eb' : '#cbd5e1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: enabled ? 'pointer' : 'not-allowed',
  transition: 'all 0.2s ease',
  boxShadow: enabled ? '0 1px 3px rgba(37, 99, 235, 0.1)' : 'none',
});

export default CardCarousel;
